import { 
  Factory, Truck, Building, BarChart3,
  Shield, Database, Zap, CheckCircle, TrendingUp, Lock, Globe, Clock,
  Target, Users, Award, Eye, AlertTriangle, FileCheck, UserCheck
} from 'lucide-react';
import { ProductConfigs } from './types';

export const manufacturingSolutionsConfigs: ProductConfigs = {
  'manufacturing-management': {
    keyBenefits: [
      {
        icon: Factory,
        title: 'Production Planning Optimization',
        description: 'AI-powered production scheduling reducing downtime by 40% and increasing throughput efficiency.',
        metrics: '40% downtime reduction',
        color: 'from-gray-500 to-slate-600'
      },
      {
        icon: Target,
        title: 'Quality Control Automation',
        description: 'Automated quality assurance with real-time defect detection improving product quality by 60%.',
        metrics: '60% quality improvement',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: TrendingUp,
        title: 'Supply Chain Optimization',
        description: 'Intelligent inventory management and supplier coordination reducing costs by 35%.',
        metrics: '35% cost reduction',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Users,
        title: 'Workforce Management',
        description: 'Advanced scheduling and skill-based task assignment improving productivity by 45%.',
        metrics: '45% productivity boost',
        color: 'from-purple-500 to-pink-500'
      },
      {
        icon: BarChart3,
        title: 'Real-Time Production Analytics',
        description: 'Comprehensive manufacturing analytics with predictive maintenance and performance insights.',
        metrics: 'Real-time insights',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Award,
        title: 'Compliance & Safety Management',
        description: 'Automated safety monitoring and regulatory compliance ensuring 100% adherence to standards.',
        metrics: '100% compliance',
        color: 'from-cyan-500 to-blue-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Eye,
        title: 'AI-Powered Predictive Maintenance',
        description: 'Machine learning predicts equipment failures 30 days in advance preventing unplanned downtime.',
        metrics: '30-day failure prediction',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: AlertTriangle,
        title: 'Real-Time Quality Monitoring',
        description: 'Computer vision and IoT sensors provide continuous quality monitoring with instant alerts.',
        metrics: 'Continuous monitoring',
        color: 'from-red-500 to-pink-500'
      },
      {
        icon: Database,
        title: 'Digital Twin Integration',
        description: 'Virtual factory modeling for process optimization and scenario testing before implementation.',
        metrics: 'Digital twin modeling',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Truck,
        title: 'Supply Chain Intelligence',
        description: 'End-to-end supply chain visibility with automated reordering and supplier performance tracking.',
        metrics: 'Supply chain visibility',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: FileCheck,
        title: 'Advanced Reporting & Analytics',
        description: 'Executive dashboards with KPI tracking, trend analysis, and automated report generation.',
        metrics: 'Advanced analytics',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: UserCheck,
        title: 'Lean Manufacturing Tools',
        description: 'Six Sigma and lean manufacturing methodologies integrated with continuous improvement tracking.',
        metrics: 'Lean manufacturing',
        color: 'from-yellow-500 to-orange-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'logistics-transportation': {
    keyBenefits: [
      {
        icon: Truck,
        title: 'Intelligent Fleet Management',
        description: 'AI-powered fleet optimization reducing fuel costs by 30% and improving delivery efficiency.',
        metrics: '30% cost reduction',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Route Optimization Engine',
        description: 'Advanced route planning with real-time traffic analysis reducing delivery time by 45%.',
        metrics: '45% faster delivery',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Users,
        title: 'Driver Performance Management',
        description: 'Comprehensive driver analytics with safety scoring and performance improvement programs.',
        metrics: 'Driver optimization',
        color: 'from-purple-500 to-pink-500'
      },
      {
        icon: BarChart3,
        title: 'Real-Time Shipment Tracking',
        description: 'End-to-end visibility with automated customer notifications and delivery confirmation.',
        metrics: 'Complete visibility',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Award,
        title: 'Warehouse Management Integration',
        description: 'Seamless warehouse operations with automated picking, packing, and inventory management.',
        metrics: 'Warehouse integration',
        color: 'from-cyan-500 to-blue-500'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Analytics Platform',
        description: 'Demand forecasting and capacity planning improving operational efficiency by 50%.',
        metrics: '50% efficiency boost',
        color: 'from-amber-500 to-orange-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Eye,
        title: 'AI-Powered Demand Forecasting',
        description: 'Machine learning predicts shipping demand patterns enabling proactive capacity planning.',
        metrics: 'Demand prediction',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: AlertTriangle,
        title: 'Predictive Vehicle Maintenance',
        description: 'IoT sensors and AI analytics predict vehicle maintenance needs preventing breakdowns.',
        metrics: 'Predictive maintenance',
        color: 'from-red-500 to-pink-500'
      },
      {
        icon: Database,
        title: 'Supply Chain Visibility Platform',
        description: 'Complete supply chain transparency with partner integration and performance monitoring.',
        metrics: 'Supply chain visibility',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Factory,
        title: 'Automated Load Optimization',
        description: 'AI-driven load planning maximizing truck capacity utilization and reducing empty miles.',
        metrics: 'Load optimization',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: FileCheck,
        title: 'Compliance & Documentation',
        description: 'Automated regulatory compliance tracking with electronic logging and document management.',
        metrics: 'Compliance automation',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: UserCheck,
        title: 'Customer Portal & API',
        description: 'Self-service customer portal with real-time tracking and integrated API for system connections.',
        metrics: 'Customer integration',
        color: 'from-yellow-500 to-orange-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'construction-project-management': {
    keyBenefits: [
      {
        icon: Building,
        title: 'Comprehensive Project Planning',
        description: 'Advanced project planning with resource allocation reducing project delays by 50%.',
        metrics: '50% delay reduction',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Users,
        title: 'Workforce Coordination Platform',
        description: 'Multi-contractor coordination with skill-based task assignment and progress tracking.',
        metrics: 'Workforce coordination',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: Target,
        title: 'Budget & Cost Control',
        description: 'Real-time budget tracking with automated cost control preventing 90% of budget overruns.',
        metrics: '90% overrun prevention',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Shield,
        title: 'Safety Compliance Management',
        description: 'Comprehensive safety monitoring with incident tracking reducing workplace accidents by 70%.',
        metrics: '70% accident reduction',
        color: 'from-red-500 to-pink-500'
      },
      {
        icon: BarChart3,
        title: 'Progress Monitoring & Reporting',
        description: 'Real-time project progress with milestone tracking and automated stakeholder reporting.',
        metrics: 'Real-time progress',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: Award,
        title: 'Quality Assurance System',
        description: 'Digital quality inspections with photo documentation ensuring 100% quality compliance.',
        metrics: '100% quality compliance',
        color: 'from-cyan-500 to-blue-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Eye,
        title: 'AI-Powered Risk Assessment',
        description: 'Machine learning identifies project risks and provides mitigation strategies proactively.',
        metrics: 'Risk prediction',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: AlertTriangle,
        title: 'Predictive Project Analytics',
        description: 'Advanced analytics predict project completion dates and identify potential bottlenecks.',
        metrics: 'Predictive analytics',
        color: 'from-red-500 to-pink-500'
      },
      {
        icon: Database,
        title: 'BIM Integration Platform',
        description: 'Full Building Information Modeling integration with 3D visualization and clash detection.',
        metrics: 'BIM integration',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Truck,
        title: 'Supply Chain & Procurement',
        description: 'Automated procurement with supplier management and material delivery optimization.',
        metrics: 'Supply optimization',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: FileCheck,
        title: 'Document Management System',
        description: 'Centralized document control with version management and automated approval workflows.',
        metrics: 'Document control',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: UserCheck,
        title: 'Mobile Field Management',
        description: 'Comprehensive mobile app for field teams with offline capabilities and real-time sync.',
        metrics: 'Mobile field access',
        color: 'from-yellow-500 to-orange-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  }
};
